import React, { Suspense } from "react";
import { lazyPage } from "./utils/lazyPage";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import {
  AuthProvider,
  ProtectedRoute,
  LoginPage,
  useAuth,
} from "./components/Auth";
import Navigation from "./components/Navigation";
import EmployeeManagement from "./components/EmployeeManagement";
import ProjectManagement from "./components/ProjectManagement";
import HomePage from "./components/Home/HomePage";
import ProgressReviewPage from "./components/ProgressReview/ProgressReviewPage";
import ConnectGooglePage from "./components/Google/ConnectGooglePage";
import ProjectDetail from "./components/ProjectDetail";
import ProjectKickoffPage from "./components/ProjectKickoffPage";
import { SlotDetail, SlotsManagement } from "./components/Slot";
import AddressManagement from "./components/AddressManagement";
import ClientManagement from "./components/ClientManagement";
import HoursManagement from "./components/HoursManagement";
import ObjectiveBoard from "./components/ObjectiveBoard";
import Dashboard from "./components/Dashboard";
import ChatBot from "./components/Chat";
import ExpenseManagement from "./components/ExpenseManagement";
import ExpensesPage from "./components/Expenses/ExpensesPage";
import BillingBatchManagement from "./components/BillingBatchManagement";
import BacklogPage from "./components/BacklogPage";
import { NexusSyncProvider } from "./context/NexusSyncContext";

import UserAiSettings from "./components/UserPreferences/UserAiSettings";
import AgentAccess from "./components/UserPreferences/AgentAccess";
import { ConsentPage } from "./components/UserPreferences/AgentConnectorConsent";
import HolacracyPage from "./components/HolacracyPage";
import InventoryManagement from "./components/InventoryManagement";
import PilotagePage from "./components/PilotagePage";
import PresenceHeartbeat from "./components/PresenceHeartbeat";
import ScreenContextPublisher from "./components/ScreenContextPublisher";
import MeetingContextBanner from "./components/Holacracy/MeetingContextBanner";
import IterationEndNotificationPage from "./components/IterationEndNotificationPage";
import ToolsPage from "./components/ToolsPage";
import FocusTechPage from "./components/FocusTech/FocusTechPage";
import FocusTechEditor from "./components/FocusTech/FocusTechEditor";
import ExtensionPage from "./components/ExtensionPage";
import AudioPage from "./components/AudioPage";
import GuttenbergHome from "./components/Guttenberg/GuttenbergHome";
import GuttenbergReportPage from "./components/Guttenberg/GuttenbergReportPage";
import KnowledgePage from "./components/Knowledge";
import OffersPage from "./components/Offers/OffersPage";
import EstimateEditor from "./components/Offers/EstimateEditor";
import ProposalEditor from "./components/Offers/proposal/ProposalEditor";
import PresentationsPage from "./components/Presentations/PresentationsPage";
import PresenterWindow from "./components/Presentations/PresenterWindow";
import PresentationAssembly from "./components/Presentations/PresentationAssembly";
import ReportsPage from "./components/Reports/ReportsPage";
import ReportEditor from "./components/Reports/ReportEditor";
import ProposalPrint from "./components/Offers/proposal/ProposalPrint";
import ProposalRender from "./components/Offers/proposal/ProposalRender";
import { MeetingDetail, MeetingRecorderProvider, MyMeetings, RecorderBar, StartMeetingDialog, TacticalMeetingRecap } from "./components/Meetings";
import MyPoints from "./components/Points/MyPoints";
import AIUsagePage from "./components/AIUsagePage";
import NexusRights from "./components/NexusRights";
import ProjectPage from "./components/ProjectPage";
import KickoffPage from "./components/Kickoff/KickoffPage";
import KickoffLaunchPage from "./components/Kickoff/KickoffLaunchPage";
import ChangeReasonHost from "./components/ChangeReasonHost";

// Portail client : chargé à part, seulement sous /portail.
const PortalApp = lazyPage(() => import("./components/Portal"));
// Sur portail.innowide.fr (portail.localhost en local), toute adresse mène au portail : l'interface interne n'y existe pas.
const onPortalHost = window.location.hostname.startsWith("portail.");
const ProjectPortalTab = lazyPage(() => import("./components/ProjectPage/tabs/PortalTab"));
const ClientPreview = lazyPage(() => import("./components/Portal/ClientPreview"));
const ModelPage = lazyPage(() => import("./components/Models/ModelPage"));

// Accueil personnalisé pour les employés activés (déploiement progressif), sinon la liste des projets.
const HomeOrProjects = () => {
  const { user } = useAuth();
  return user?.can_use_home ? <HomePage /> : <ProjectManagement />;
};

// Nouvelle fiche projet pour les employés activés (déploiement progressif) ; « ?ancienne=1 » rouvre l'ancienne.
const ProjectRoute = () => {
  const { user } = useAuth();
  const old = new URLSearchParams(useLocation().search).get("ancienne") === "1";
  return user?.can_use_new_project && !old ? <ProjectPage /> : <ProjectDetail />;
};

// Nouveau kickoff (mêmes personnes que la nouvelle fiche) ; « ?ancienne=1 » rouvre l'ancien.
const KickoffRoute = () => {
  const { user } = useAuth();
  const old = new URLSearchParams(useLocation().search).get("ancienne") === "1";
  return user?.can_use_new_project && !old ? <KickoffPage /> : <ProjectKickoffPage />;
};

// Kickoff de lancement d'un projet en proposition (réunion en direct avec Nexus) : mêmes personnes que la nouvelle fiche.
const KickoffLaunchRoute = () => {
  const { user } = useAuth();
  const { projectId } = useParams();
  return user?.can_use_new_project ? <KickoffLaunchPage /> : <Navigate to={`/project/${projectId}/kickoff`} replace />;
};

// Lien court imprimé en QR code sur une pièce ou une carte (une version du PLM) : ouvre sa fiche dans le PLM.
const PlmVersionLink = () => {
  const { itemId, version } = useParams();
  return <Navigate to={`/inventory?item=${encodeURIComponent(itemId)}&version=${encodeURIComponent(version)}`} replace />;
};

// Portail client d'un projet (échanges, invitations, publications) pour toute l'équipe, nouvelle fiche ou non :
// c'est là que mènent les messages de Nexus quand un client écrit.
const ProjectPortalRoute = () => {
  const { projectId } = useParams();
  return (
    <div className="mx-auto max-w-[1500px] space-y-3 px-1 py-4 sm:px-4 lg:px-6">
      <a href={`/project/${projectId}`} className="text-sm font-semibold text-indigo-600 hover:underline">← Projet</a>
      <h1 className="text-xl font-semibold text-slate-900">Portail client</h1>
      <Suspense fallback={null}><ProjectPortalTab projectId={Number(projectId)} /></Suspense>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;
  if (!user?.is_admin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Pilotage : administrateurs et comptes ayant le droit « Pilotage ».
const PilotageRoute = ({ children }) => {
  const { user } = useAuth();
  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;
  if (!user?.is_admin && !isSuperAdmin && !user?.can_view_pilotage) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  if (onPortalHost) {
    return (
      <Router>
        <Routes>
          <Route path="/*" element={<Suspense fallback={null}><PortalApp /></Suspense>} />
        </Routes>
      </Router>
    );
  }
  return (
    <Router>
      <Routes>
        {/* Portail client : cadre à part, hors de la session et de l'interface internes (voir components/Portal). */}
        <Route path="/portail/*" element={<Suspense fallback={null}><PortalApp /></Suspense>} />
        <Route path="*" element={
      <AuthProvider>
        <NexusSyncProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Consentement OAuth d'un connecteur MCP (claude.ai, Claude Code) : page seule, ouverte depuis le client. */}
            <Route path="/agent-access/connecteur" element={<ConsentPage />} />
            {/* Rendu des slides pour les agents (Chrome headless) : jeton signé, sans session. */}
            <Route path="/tools/offres/rendu/:token" element={<ProposalRender />} />
            {/* Fenêtre de présentation à partager en visio : les slides seules, sans menu, notifications ni Nexus. */}
            <Route path="/presenter/presentations/:proposalId" element={<ProtectedRoute><PresenterWindow /></ProtectedRoute>} />
            <Route path="/presenter/points/:proposalId" element={<ProtectedRoute><PresenterWindow kind="point" key="point" /></ProtectedRoute>} />
            {/* Impression d'une proposition : hors du cadre de l'application. */}
            <Route
              path="/tools/offres/propositions/:proposalId/imprimer"
              element={<ProtectedRoute><ProposalPrint /></ProtectedRoute>}
            />

            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MeetingRecorderProvider>
                  <div className="min-h-screen bg-gray-100">
                    <PresenceHeartbeat />
                    <ScreenContextPublisher />
                    <Navigation />
                    <MeetingContextBanner />
                    <main className="py-1">
                      <div className="max-w-full mx-auto px-1 sm:px-6 lg:px-8">
                        <Routes>
                          <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
                          <Route path="/employees" element={<AdminRoute><EmployeeManagement /></AdminRoute>} />
                          <Route path="/projects" element={<ProjectManagement />} />
                          <Route path="/project/:projectId" element={<ProjectRoute />} />
                          <Route path="/meetings" element={<MyMeetings />} />
                          <Route path="/points" element={<MyPoints />} />
                          <Route path="/revue-avancement" element={<ProgressReviewPage />} />
                          <Route path="/relier-google" element={<ConnectGooglePage />} />
                          <Route path="/meetings/:meetingId" element={<MeetingDetail />} />
                          <Route path="/meetings/tactique/:meetingId" element={<TacticalMeetingRecap />} />
                          <Route path="/project/:projectId/kickoff" element={<KickoffRoute />} />
                          <Route path="/project/:projectId/portail" element={<ProjectPortalRoute />} />
                          <Route path="/project/:projectId/vue-client/*" element={<Suspense fallback={null}><ClientPreview /></Suspense>} />
                          <Route path="/project/:projectId/modeles/:modelId" element={<Suspense fallback={null}><ModelPage /></Suspense>} />
                          <Route path="/project/:projectId/kickoff/lancement" element={<KickoffLaunchRoute />} />
                          <Route path="/slots" element={<SlotsManagement displayMode="timeline" />} />
                          <Route path="/slots/current" element={<SlotsManagement displayMode="current" />} />
                          <Route path="/slot/:slotId" element={<SlotDetail />} />
                          <Route
                            path="/hours"
                            element={
                              <div className="max-w-6xl mx-auto">
                                <HoursManagement />
                              </div>
                            }
                          />
                          <Route path="/clients" element={<ClientManagement />} />
                          <Route path="/addresses" element={<AddressManagement />} />
                          <Route path="/objectives" element={<ObjectiveBoard />} />
                          <Route path="/" element={<HomeOrProjects />} />
                          <Route path="/backlog" element={<BacklogPage />} />
                          <Route path="/expenses" element={<ExpensesPage />} />
                          <Route path="/expenses/classique" element={<ExpenseManagement />} />
                          <Route path="/billing-batches" element={<BillingBatchManagement />} />
                          <Route path="/iteration-end-notifications/:eventId" element={<IterationEndNotificationPage />} />
                          <Route path="/holacracy" element={<HolacracyPage />} />
                          <Route path="/ai-preferences" element={<UserAiSettings />} />
                          <Route path="/agent-access" element={<AgentAccess />} />
                          <Route path="/inventory" element={<InventoryManagement />} />
                          <Route path="/plm/:itemId/:version" element={<PlmVersionLink />} />
                          <Route path="/pilotage" element={<PilotageRoute><PilotagePage /></PilotageRoute>} />
                          <Route path="/ai-usage" element={<AdminRoute><AIUsagePage /></AdminRoute>} />
                          <Route path="/nexus-rights" element={<AdminRoute><NexusRights /></AdminRoute>} />
                          <Route path="/tools" element={<ToolsPage />} />
                          <Route path="/tools/extension" element={<ExtensionPage />} />
                          <Route path="/audio/:audioId" element={<AudioPage />} />
                          <Route path="/tools/focus-tech" element={<FocusTechPage />} />
                          <Route path="/tools/focus-tech/:focusId" element={<FocusTechEditor />} />
                          <Route path="/tools/knowledge" element={<KnowledgePage />} />
                          <Route path="/tools/guttenberg" element={<GuttenbergHome />} />
                          <Route path="/tools/offres" element={<OffersPage />} />
                          <Route path="/tools/offres/:estimateId" element={<EstimateEditor />} />
                          <Route path="/tools/offres/propositions/:proposalId" element={<ProposalEditor />} />
                          <Route path="/tools/presentations" element={<PresentationsPage />} />
                          <Route path="/tools/presentations/points/:assemblyId" element={<PresentationAssembly />} />
                          <Route path="/tools/presentations/:proposalId" element={<ProposalEditor kind="presentation" key="presentation" />} />
                          <Route path="/tools/rapports" element={<ReportsPage />} />
                          <Route path="/tools/rapports/:reportId" element={<ReportEditor />} />
                          <Route path="/tools/guttenberg/reports/:projectId" element={<GuttenbergReportPage />} />
                        </Routes>
                      </div>
                    </main>
                    {/* Chatbot */}
                    <ChatBot />
                    {/* Enregistrement de réunion : continue quand on change de page */}
                    <RecorderBar />
                    <StartMeetingDialog />
                    {/* Motif d'un changement de roadmap ou de budget exigé par le serveur */}
                    <ChangeReasonHost />
                    <footer className="bg-white border-t border-gray-200">
                      <div className="max-w-full mx-auto py-6 px-4 sm:px-6 lg:px-8">
                        <p className="text-center text-sm text-gray-500">
                          © {new Date().getFullYear()} Innowide DeepFlow. Tous droits réservés.
                        </p>
                      </div>
                    </footer>
                  </div>
                  </MeetingRecorderProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
        </NexusSyncProvider>
      </AuthProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
